<template>
    <div class="space-y-2">
        <div>
            <Card>
                <template #content>
                    <div class="space-y-8">
                        <div>
                            <SectionTitle> Preview </SectionTitle>
                            <a
                                :href="localEmbedButtonSettings.destinationUrl"
                                target="_blank"
                                :style="`
                                    text-decoration: none;
                                    padding: 10px 20px;
                                    font-weight: ${localEmbedButtonSettings.fontWeight};
                                    font-size: ${localEmbedButtonSettings.fontSize}px;
                                    border-radius: ${localEmbedButtonSettings.borderRadius}px;
                                    background-color: ${localEmbedButtonSettings.backgroundColor};
                                    color: ${localEmbedButtonSettings.textColor};
                                    display: inline-flex;
                                    align-items: center;
                                    gap: 0.7rem;
                                `"
                                onmouseover="this.style.opacity = '0.95'"
                                onmouseout="this.style.opacity = '1'"
                            >
                                <span class="items-center gap-2">
                                    {{ localEmbedButtonSettings.textContent }}
                                </span>
                                <span v-if="localEmbedButtonSettings.showIcon">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        :width="
                                            localEmbedButtonSettings.fontSize *
                                            0.9
                                        "
                                        :height="
                                            localEmbedButtonSettings.fontSize *
                                            0.9
                                        "
                                        fill="currentColor"
                                        viewBox="0 0 512 512"
                                    >
                                        <!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc -->
                                        <path
                                            d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3l0 82.7c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z"
                                        />
                                    </svg>
                                </span>
                            </a>
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <Button
                                    @click="copyTopEmbedCodeToClipboard"
                                    color="light"
                                    size="small"
                                    iconName="copy"
                                    iconClass="w-4 h-4"
                                    :disabled="cannotCopyToClipboard"
                                >
                                    Copy Embed Code
                                </Button>
                                <Button
                                    @click="showSettings = !showSettings"
                                    color="light"
                                    size="small"
                                    :iconName="
                                        showSettings ? 'angle-up' : 'angle-down'
                                    "
                                    iconClass="w-4 h-4"
                                    :iconRight="true"
                                >
                                    Customize
                                </Button>
                            </div>
                            <div
                                v-if="
                                    messagePosition ===
                                    StatusMessagePosition.Top
                                "
                            >
                                <span
                                    :class="{ hidden: !showCopiedMessage }"
                                    class="text-primary text-sm"
                                >
                                    Embed code copied!
                                </span>
                                <span
                                    :class="{ hidden: !showErrorMessage }"
                                    class="text-red-500 text-sm"
                                >
                                    Error trying to copy embed code.
                                </span>
                            </div>
                        </div>

                        <template v-if="showSettings">
                            <div class="border-b border-b-gray-200"></div>

                            <div>
                                <SectionTitle> Styling </SectionTitle>
                                <ApiForm
                                    method="POST"
                                    :url="
                                        urlFactory.makeCompanyUpdateEmbedButtonSettingsUrl()
                                    "
                                    ref="apiFormRef"
                                >
                                    <div class="grid grid-cols-12 gap-4">
                                        <div
                                            class="col-span-12 lg:col-span-6 space-y-4"
                                        >
                                            <FormControl label="Text">
                                                <TextInput
                                                    v-model="
                                                        localEmbedButtonSettings.textContent
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_TextContent
                                                    "
                                                />
                                            </FormControl>
                                            <FormControl
                                                label="Background color"
                                            >
                                                <ColorInput
                                                    v-model="
                                                        localEmbedButtonSettings.backgroundColor
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_BackgroundColor
                                                    "
                                                />
                                            </FormControl>
                                            <FormControl label="Text color">
                                                <ColorInput
                                                    v-model="
                                                        localEmbedButtonSettings.textColor
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_TextColor
                                                    "
                                                />
                                            </FormControl>
                                            <FormControl
                                                label="Icon visibility"
                                            >
                                                <CheckboxInput
                                                    label="Show Icon"
                                                    :checked="
                                                        localEmbedButtonSettings.showIcon
                                                    "
                                                    v-model="
                                                        localEmbedButtonSettings.showIcon
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_ShowIcon
                                                    "
                                                />
                                            </FormControl>
                                        </div>
                                        <div
                                            class="col-span-12 lg:col-span-6 space-y-4"
                                        >
                                            <FormControl label="Font weight">
                                                <SelectInput
                                                    v-model="
                                                        localEmbedButtonSettings.fontWeight
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_FontWeight
                                                    "
                                                >
                                                    <option :value="400">
                                                        Thin
                                                    </option>
                                                    <option :value="500">
                                                        Normal
                                                    </option>
                                                    <option :value="600">
                                                        Semibold
                                                    </option>
                                                    <option :value="700">
                                                        Bold
                                                    </option>
                                                </SelectInput>
                                            </FormControl>
                                            <FormControl label="Font size">
                                                <TextInput
                                                    type="number"
                                                    mode="numeric"
                                                    min="10"
                                                    step="1"
                                                    v-model="
                                                        localEmbedButtonSettings.fontSize
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_FontSize
                                                    "
                                                />
                                            </FormControl>
                                            <FormControl label="Border radius">
                                                <TextInput
                                                    type="number"
                                                    mode="numeric"
                                                    min="0"
                                                    step="1"
                                                    v-model="
                                                        localEmbedButtonSettings.borderRadius
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_BorderRadius
                                                    "
                                                />
                                            </FormControl>
                                        </div>
                                        <div class="col-span-12">
                                            <FormControl label="Link to">
                                                <SelectInput
                                                    v-model="
                                                        localEmbedButtonSettings.destinationUrl
                                                    "
                                                    :name="
                                                        RequestBodyKeyEnum.App_Company_EmbedButtonSettings_DestinationUrl
                                                    "
                                                >
                                                    <template
                                                        v-for="destinationUrl in destinationUrls"
                                                    >
                                                        <option
                                                            :value="
                                                                destinationUrl.url
                                                            "
                                                        >
                                                            {{
                                                                destinationUrl.title
                                                            }}
                                                            <span
                                                                v-if="
                                                                    destinationUrl.isDisabled
                                                                "
                                                                >(Disabled)</span
                                                            >
                                                        </option>
                                                    </template>
                                                </SelectInput>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div class="flex justify-end mt-4">
                                        <Button
                                            @click="apiFormRef?.submit()"
                                            :loading="
                                                apiFormRef?.loading.valueOf()
                                            "
                                            iconName="save"
                                            color="light"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </ApiForm>
                            </div>

                            <div class="border-b border-b-gray-200"></div>

                            <div class="space-y-2">
                                <SectionTitle> HTML Code </SectionTitle>
                                <TextareaInput
                                    class="text-xs"
                                    rows="6"
                                    v-model="embedCode"
                                />
                                <div class="text-sm text-dark">
                                    <strong>Note:</strong> The button's text
                                    font will use whatever font your website is
                                    setup to use, so it will most likely look
                                    different compared to the preview.
                                </div>
                                <div class="flex justify-end">
                                    <div class="flex flex-col items-end">
                                        <Button
                                            class="inline"
                                            color="light"
                                            iconName="copy"
                                            :disabled="cannotCopyToClipboard"
                                            @click="
                                                copyBottomEmbedCodeToClipboard
                                            "
                                        >
                                            Copy
                                        </Button>
                                        <div
                                            v-if="
                                                messagePosition ===
                                                StatusMessagePosition.Bottom
                                            "
                                        >
                                            <span
                                                :class="{
                                                    hidden: !showCopiedMessage,
                                                }"
                                                class="text-primary text-sm"
                                            >
                                                Embed code copied!
                                            </span>
                                            <span
                                                :class="{
                                                    hidden: !showErrorMessage,
                                                }"
                                                class="text-red-500 text-sm"
                                            >
                                                Error trying to copy embed code.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import { computed, ref, type PropType } from 'vue'

    import useCopyToClipboard from '@/Shared/Hook/useCopyToClipboard'
    import useRecordAppClipboardAnalyticsEvent from '@/Shared/Hook/useRecordAppClipboardAnalyticsEvent'
    import { AnalyticsEventSurfaceEnum } from '@/Shared/AutoGenerated/Main'

    import Card from '@/Shared/Component/Card.vue'
    import Button from '@/Shared/Component/Button.vue'

    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import ColorInput from '@/Shared/Component/Forms/ColorInput.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import TextareaInput from '@/Shared/Component/Forms/TextareaInput.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'

    import CheckboxInput from '@/Shared/Component/Forms/CheckboxInput.vue'
    import type {
        ApiFormRefType,
        EmbedButtonDestinationUrl,
    } from '@/Shared/AutoGenerated/Types'
    import { type EmbedButtonSettingsType } from '@/Shared/AutoGenerated/Main'

    import Company from '@/App/Company/Entity/Company'
    import SectionTitle from '@/App/Company/Component/SectionTitle.vue'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

    const props = defineProps({
        destinationUrls: {
            type: Array as PropType<EmbedButtonDestinationUrl[]>,
            required: true,
        },
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
    })

    // -----------------------------------
    // Button designer and preview
    // -----------------------------------

    const showSettings = ref<boolean>(false)

    const localEmbedButtonSettings = ref<EmbedButtonSettingsType>(
        props.company.embedButtonSettings,
    )
    localEmbedButtonSettings.value.destinationUrl =
        props.destinationUrls[0].url ?? ''

    const embedCode = computed(() => {
        const iconHtml = localEmbedButtonSettings.value.showIcon
            ? `<span><svg xmlns="http://www.w3.org/2000/svg" width="${localEmbedButtonSettings.value.fontSize * 0.9}" height="${localEmbedButtonSettings.value.fontSize * 0.9}" fill="currentColor" viewBox="0 0 512 512"><!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc --><path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3l0 82.7c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 32C35.8 32 0 67.8 0 112L0 432c0 44.2 35.8 80 80 80l320 0c44.2 0 80-35.8 80-80l0-112c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 112c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-320c0-8.8 7.2-16 16-16l112 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 32z"/></svg></span>`
            : ''

        return `<a
    href="${localEmbedButtonSettings.value.destinationUrl}"
    target="_blank"
    style="
        text-decoration: none;
        padding: 10px 20px;
        font-weight: ${localEmbedButtonSettings.value.fontWeight};
        font-size: ${localEmbedButtonSettings.value.fontSize}px;
        border-radius: ${localEmbedButtonSettings.value.borderRadius}px;
        background-color: ${localEmbedButtonSettings.value.backgroundColor};
        color: ${localEmbedButtonSettings.value.textColor};
        display: inline-flex;
        align-items: center;
        gap: 0.7rem;
    "
    onmouseover="this.style.opacity='0.95'"
    onmouseout="this.style.opacity='1'"
>
    <span class="items-center gap-2">
        ${localEmbedButtonSettings.value.textContent}
    </span>
    ${iconHtml}
</a>`
    })

    // -----------------------------------
    // Copy to clipboard
    // -----------------------------------

    enum StatusMessagePosition {
        Top = 'Top',
        Bottom = 'Bottom',
    }

    const copyToClipboard = useCopyToClipboard()
    const cannotCopyToClipboard = ref<boolean>(copyToClipboard.isNotAvailable())
    const showCopiedMessage = ref<boolean>(false)
    const showErrorMessage = ref<boolean>(false)

    let timeoutHandler: any = undefined

    const messagePosition = ref<StatusMessagePosition>(
        StatusMessagePosition.Top,
    )

    function copyTopEmbedCodeToClipboard(): void {
        copyEmbedCodeToClipboard(StatusMessagePosition.Top)
    }

    function copyBottomEmbedCodeToClipboard(): void {
        copyEmbedCodeToClipboard(StatusMessagePosition.Bottom)
    }

    function copyEmbedCodeToClipboard(position: StatusMessagePosition): void {
        showCopiedMessage.value = false
        showErrorMessage.value = false

        messagePosition.value = position

        if (timeoutHandler !== undefined) {
            clearTimeout(timeoutHandler)
            timeoutHandler = undefined
        }

        copyToClipboard.copy(
            embedCode.value,
            () => {
                showCopiedMessage.value = true

                recordAnalyticsEvent()

                timeoutHandler = setTimeout(() => {
                    showCopiedMessage.value = false
                }, 1000)
            },
            () => {
                showErrorMessage.value = true

                timeoutHandler = setTimeout(() => {
                    showErrorMessage.value = false
                }, 1000)
            },
        )
    }

    // --------------
    // Persist button settings form request
    // --------------

    const apiFormRef = ref<ApiFormRefType>(null)

    // --------------
    // Analytics
    // --------------

    const recordAppClipboardAnalyticsEvent =
        useRecordAppClipboardAnalyticsEvent()

    const recordAnalyticsEvent = () => {
        recordAppClipboardAnalyticsEvent.recordEmbedCodeCopied(
            AnalyticsEventSurfaceEnum.Copy_EmbedButton_Designer,
        )
    }
    const urlFactory = new UrlFactory()
</script>
